/**
 * Monte Seu Kit V2 — assets/monte-seu-kit.js
 *
 * Custom element <monte-seu-kit> orquestra:
 * - State: Map<variantId, { qty, kitPrice, avulsoPrice, title, image, handle }>
 * - Eventos do <quantity-selector> do tema (input event no <input is="quantity-input">)
 * - Submit POST /cart/add.js
 * - Persistência localStorage TTL 24h
 * - Auto-abre drawer mobile na 1ª adição (Fase 2 — comentado por ora)
 *
 * Reusa Intl.NumberFormat pt-BR para formatação de preço.
 */

(function () {
  'use strict';

  if (window.customElements.get('monte-seu-kit')) return;

  const STORAGE_KEY = 'lsn:monte-seu-kit:v2';
  const TTL_MS = 24 * 60 * 60 * 1000;

  const fmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
  const money = (cents) => fmt.format((cents || 0) / 100);

  function uuid() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  class MonteSeuKit extends HTMLElement {
    constructor() {
      super();
      this.state = new Map();
      this.submitting = false;
    }

    connectedCallback() {
      this.kitSize = parseInt(this.getAttribute('data-kit-size'), 10) || 6;
      this.kitLabel = this.getAttribute('data-kit-label') || 'Monte Seu Kit';
      this.kitUnit = this.getAttribute('data-kit-unit-label') || 'mini';
      this.autoOpenDrawer = this.getAttribute('data-auto-open-drawer') !== 'false';
      // storageKey inclui kitUnit pra evitar colisao entre instancias diferentes
      // (ex: minis kit_size=6 vs polpas kit_size=2 — keys distintas).
      this.storageKey = `${STORAGE_KEY}:${this.kitUnit}:${this.kitSize}`;
      // IntersectionObserver: sticky bar mobile so visivel quando o <monte-seu-kit>
      // esta no viewport. Sem isso, sticky bar ficaria flutuando em outras paginas
      // se o state localStorage tiver items.
      this._inViewport = true; // default: assume visible (eh atualizado pelo observer)
      this._observer = new IntersectionObserver(
        (entries) => {
          this._inViewport = entries[0]?.isIntersecting ?? false;
          this.render();
        },
        { rootMargin: '0px' }
      );
      this._observer.observe(this);

      this.cards = Array.from(this.querySelectorAll('[data-kit-card]'));

      // Drawer pode já ter migrado para document.body (theme.js Popover.shouldAppendToBody=md-max).
      // Tentamos achar por ID (via aria-controls do botão Ver kit) antes de cair no querySelector local.
      this.drawer = this.querySelector('[data-kit-drawer]');
      if (!this.drawer) {
        const opener = this.querySelector('[data-kit-open-drawer][aria-controls]');
        const drawerId = opener?.getAttribute('aria-controls');
        if (drawerId) this.drawer = document.getElementById(drawerId);
      }

      // Override de focus-trap behavior pra evitar que cliques em cards / sticky bar
      // (após o drawer migrar pra body em mobile) fechem o drawer involuntariamente.
      //
      // Por que 2 overrides:
      // 1. `hideForOutsideClickTarget` — método chamado em allowOutsideClickMouse_fn (theme.js
      //    L2009). Retornar false impede chamada explícita de hide() ali.
      // 2. `allowOutsideClickForTarget` — método chamado logo em seguida (theme.js L2012).
      //    O retorno desta função é o que `allowOutsideClick` retorna pro focus-trap.
      //    Padrão é `false` → focus-trap interpreta como "outside click bloqueado" e DEACTIVATES,
      //    o que dispara `onDeactivate: this.hide.bind(this)` (L1872) e fecha o drawer.
      //    Retornando true: permite o click fora, focus-trap não deactivates, drawer permanece aberto.
      // Drawer só fecha via close button do header, ESC, ou drawer.hide() programático.
      if (this.drawer) {
        this.drawer.hideForOutsideClickTarget = () => false;
        this.drawer.allowOutsideClickForTarget = () => true;
      }

      this.bindCards();
      this.bindControls();
      this.restore();
      this.render();
    }

    // Em mobile, o <x-popover> move-se para document.body (theme.js L2149 shouldAppendToBody=md-max).
    // Isso desconecta o drawer do <monte-seu-kit> parent. Resultado: this.querySelectorAll não acha
    // elementos dentro do drawer. Usamos getRoots() para iterar ambos: <monte-seu-kit> + drawer.
    getRoots() {
      const roots = [this];
      if (this.drawer && !this.contains(this.drawer)) roots.push(this.drawer);
      return roots;
    }

    queryAllInRoots(selector) {
      const els = [];
      this.getRoots().forEach((root) => {
        els.push(...root.querySelectorAll(selector));
      });
      return els;
    }

    bindCards() {
      this.cards.forEach((card) => {
        const addBtn = card.querySelector('[data-kit-add]');
        const qtyControl = card.querySelector('[data-kit-qty-control]');
        const qtyInput = qtyControl?.querySelector('input[is="quantity-input"]');
        const figure = card.querySelector('[data-kit-image-add]');

        if (addBtn) {
          addBtn.addEventListener('click', (e) => {
            e.preventDefault();
            this.setQty(card, 1);
          });
        }

        // Click na foto adiciona +1 unidade ao card (mesma logica do botao + do
        // quantity-selector). Clamp pelo kitSize remaining.
        if (figure) {
          figure.addEventListener('click', (e) => {
            if (card.dataset.outOfStock === 'true') return;
            // Guard primario: kit ja cheio bloqueia qualquer card (incluindo iniciar state em qty=0).
            if (this.totalQty() >= this.kitSize) return;
            const stateQty = this.state.get(card.dataset.variantId)?.qty || 0;
            const others = this.totalQty() - stateQty;
            const maxAllowed = this.kitSize - others;
            if (stateQty >= maxAllowed) return;
            e.stopPropagation();
            this.setQty(card, stateQty + 1);
          });
        }

        if (qtyControl && qtyInput) {
          // O <quantity-selector> do tema chama input.stepDown()/stepUp() ao clicar nos botões.
          // stepDown/stepUp nativos NÃO disparam evento "input".
          // Meu listener pode rodar ANTES do listener do tema (parent connectedCallback first).
          // Por isso uso `queueMicrotask` para ler o valor APÓS todos os handlers de click do
          // mesmo evento processarem (microtasks rodam após event handlers do mesmo tick).
          const decreaseBtn = qtyControl.querySelector('button:first-of-type');
          const increaseBtn = qtyControl.querySelector('button:last-of-type');

          const readAfterTheme = (callback) => {
            queueMicrotask(() => callback(parseInt(qtyInput.value, 10) || 0));
          };

          if (decreaseBtn) {
            decreaseBtn.addEventListener('click', () => {
              readAfterTheme((cur) => {
                const stateQty = this.state.get(card.dataset.variantId)?.qty || 0;
                // Se o tema clampou em 1 (min) e estávamos em 1, remover (qty=0).
                if (cur <= 1 && stateQty <= 1) {
                  qtyInput.value = 0;
                  this.setQty(card, 0);
                } else {
                  this.setQty(card, cur);
                }
              });
            });
          }

          if (increaseBtn) {
            increaseBtn.addEventListener('click', () => {
              readAfterTheme((cur) => {
                const others = this.totalQty() - (this.state.get(card.dataset.variantId)?.qty || 0);
                const maxAllowed = Math.max(0, this.kitSize - others);
                if (cur > maxAllowed) {
                  qtyInput.value = maxAllowed;
                  this.setQty(card, maxAllowed);
                } else {
                  this.setQty(card, cur);
                }
              });
            });
          }

          // Digitação manual no input
          qtyInput.addEventListener('input', () => {
            const n = parseInt(qtyInput.value, 10) || 0;
            const others = this.totalQty() - (this.state.get(card.dataset.variantId)?.qty || 0);
            const maxAllowed = Math.max(0, this.kitSize - others);
            const capped = Math.max(0, Math.min(n, maxAllowed));
            if (capped !== n) qtyInput.value = capped;
            this.setQty(card, capped);
          });
        }
      });
    }

    bindControls() {
      // Submit/clear podem estar no painel desktop (dentro de <monte-seu-kit>) OU
      // no drawer (que mobile move pra document.body). Bindamos em todos os roots.
      this.queryAllInRoots('[data-kit-submit]').forEach((b) =>
        b.addEventListener('click', () => this.submit())
      );
      this.queryAllInRoots('[data-kit-clear]').forEach((b) =>
        b.addEventListener('click', () => this.clearAll())
      );
      // [data-kit-open-drawer] NAO precisa handler proprio — o tema (theme.js L1751/2024)
      // ja delega click em [aria-controls="<id>"] via DialogElement._delegate, fazendo toggle
      // (hide se aberto, show se fechado). Ter handler proprio chama show() e o tema chama hide()
      // logo em seguida — efeito cancelado.
    }

    totalQty() {
      let n = 0;
      this.state.forEach((v) => (n += v.qty));
      return n;
    }

    totalCents() {
      let c = 0;
      this.state.forEach((v) => (c += v.kitPrice * v.qty));
      return c;
    }

    totalAvulsoCents() {
      let c = 0;
      this.state.forEach((v) => (c += v.avulsoPrice * v.qty));
      return c;
    }

    setQty(card, qty) {
      const id = card.dataset.variantId;
      if (!id) return;

      if (qty <= 0) {
        this.state.delete(id);
        this._setCardUI(card, 0);
      } else {
        const prev = this.state.get(id) || {};
        this.state.set(id, {
          ...prev,
          qty,
          kitPrice: parseInt(card.dataset.kitPrice, 10) || 0,
          avulsoPrice: parseInt(card.dataset.avulsoPrice, 10) || 0,
          title: card.dataset.productTitle || '',
          image: card.dataset.imageUrl || '',
          handle: card.dataset.productHandle || '',
        });
        this._setCardUI(card, qty);
      }

      const total = this.totalQty();
      // Auto-abre drawer mobile na 1ª adição (apenas mobile)
      if (
        qty > 0 &&
        total === qty &&
        this.autoOpenDrawer &&
        this.drawer &&
        typeof this.drawer.show === 'function' &&
        window.matchMedia('(max-width: 999px)').matches
      ) {
        this.drawer.show();
      }

      this.render();
      this.persist();
    }

    _setCardUI(card, qty) {
      card.dataset.qty = String(qty);
      const addBtn = card.querySelector('[data-kit-add]');
      const qtyControl = card.querySelector('[data-kit-qty-control]');
      const qtyInput = qtyControl?.querySelector('input[is="quantity-input"]');
      const badge = card.querySelector('[data-kit-qty-badge]');

      if (qty <= 0) {
        if (addBtn) addBtn.hidden = false;
        if (qtyControl) qtyControl.hidden = true;
        if (badge) badge.textContent = '';
        if (qtyInput) qtyInput.value = 0;
      } else {
        if (addBtn) addBtn.hidden = true;
        if (qtyControl) qtyControl.hidden = false;
        if (qtyInput && parseInt(qtyInput.value, 10) !== qty) qtyInput.value = qty;
        if (badge) badge.textContent = String(qty);

        // O tema do quantity-selector desabilita o "−" quando qty<=1.
        // Re-habilitamos manualmente para permitir reduzir abaixo de 1 (volta a 0/Adicionar).
        const decreaseBtn = qtyControl?.querySelector('button:first-of-type');
        if (decreaseBtn) decreaseBtn.disabled = false;
      }
    }

    // Atualiza estado disabled dos botões + (e dos data-kit-add) baseado na capacidade restante.
    // Evita o "piscar" quando kit está cheio e cliente clica + (tema chamava stepUp e nós clampávamos depois).
    _refreshAvailability() {
      const total = this.totalQty();
      const remaining = this.kitSize - total;

      this.cards.forEach((card) => {
        if (card.dataset.outOfStock === 'true') return;

        const stateQty = this.state.get(card.dataset.variantId)?.qty || 0;
        const addBtn = card.querySelector('[data-kit-add]');
        const qtyControl = card.querySelector('[data-kit-qty-control]');
        const decreaseBtn = qtyControl?.querySelector('button:first-of-type');
        const increaseBtn = qtyControl?.querySelector('button:last-of-type');
        const qtyInput = qtyControl?.querySelector('input[is="quantity-input"]');

        // Botão + Adicionar (card sem qty): disabled se kit já está cheio
        if (addBtn) addBtn.disabled = remaining <= 0;

        // Botão + dentro do quantity-selector: disabled se este card não pode crescer mais
        // (ou seja, se aumentar 1 ultrapassaria kit_size).
        const wouldExceed = stateQty > 0 && remaining <= 0;
        if (increaseBtn) increaseBtn.disabled = wouldExceed;

        // Input central também fica disabled quando kit completo — sinaliza visualmente
        // (texto cinza via CSS), evita digitar valor maior que o permitido.
        if (qtyInput) qtyInput.disabled = wouldExceed;

        // Botão − sempre habilitado quando qty>=1 (já garantido em _setCardUI), mas
        // reasseguramos aqui caso _updateUI do tema tenha rodado depois.
        if (decreaseBtn && stateQty >= 1) decreaseBtn.disabled = false;
      });
    }

    clearAll() {
      this.cards.forEach((c) => this._setCardUI(c, 0));
      this.state.clear();
      this.render();
      this.persist();
    }

    render() {
      const total = this.totalQty();
      const remaining = this.kitSize - total;
      const totalCents = this.totalCents();
      const totalAvulsoCents = this.totalAvulsoCents();

      this.queryAllInRoots('[data-kit-counter]').forEach((el) => (el.textContent = String(total)));
      this.queryAllInRoots('[data-kit-target]').forEach((el) => (el.textContent = String(this.kitSize)));
      this.queryAllInRoots('[data-kit-total]').forEach((el) => (el.textContent = money(totalCents)));
      this.queryAllInRoots('[data-kit-total-avulso]').forEach((el) => {
        if (totalAvulsoCents > totalCents && total > 0) {
          el.textContent = money(totalAvulsoCents);
          el.hidden = false;
        } else {
          el.hidden = true;
        }
      });

      // Progress bar
      const pct = Math.min(100, (total / this.kitSize) * 100);
      this.queryAllInRoots('[data-kit-progress-fill]').forEach((el) => {
        el.style.width = `${pct}%`;
        el.dataset.state = remaining < 0 ? 'over' : remaining === 0 ? 'ok' : 'low';
      });

      // Status text — plural simples (sufixo 's' funciona pra mini→minis, polpa→polpas, frasco→frascos)
      const unitPlural = `${this.kitUnit}s`;
      this.queryAllInRoots('[data-kit-status]').forEach((el) => {
        if (remaining > 0) {
          el.textContent = `Faltam ${remaining} ${remaining === 1 ? this.kitUnit : unitPlural}`;
          el.dataset.state = 'low';
        } else if (remaining === 0) {
          el.textContent = 'Pronto!';
          el.dataset.state = 'ok';
        } else {
          el.textContent = `Você passou ${-remaining}. Reduza ${-remaining}.`;
          el.dataset.state = 'over';
        }
      });

      // Submit button
      this.queryAllInRoots('[data-kit-submit]').forEach((b) => {
        const ready = remaining === 0 && !this.submitting;
        b.disabled = !ready;
        b.setAttribute('aria-disabled', String(!ready));
        b.textContent = this.submitting ? 'Adicionando...' : 'Adicionar ao carrinho';
      });

      // Sticky bar visibility: aparece apenas se ha state E section em viewport.
      // Quando user scrolla pra fora da page, sticky some — quando volta, reaparece.
      this.querySelectorAll('[data-kit-sticky-bar]').forEach((el) => {
        el.classList.toggle('is-visible', total > 0 && this._inViewport);
      });

      this._refreshAvailability();
      this.renderItems();
    }

    // Reconciliacao DOM em vez de innerHTML.
    // Re-criar todos <li> via innerHTML re-dispara `animation: kit-item-enter` em CADA item
    // (mesmo os que nao mudaram), causando flicker visivel ao remover/alterar qty.
    // Solucao: remover apenas os <li> orphans (state nao tem mais), atualizar texto-qty dos
    // existentes, e criar APENAS os novos. Animacao enter so dispara pra item recem-adicionado.
    renderItems() {
      const lists = this.queryAllInRoots('[data-kit-items]');
      if (lists.length === 0) return;

      const stateIds = new Set(this.state.keys());

      lists.forEach((ul) => {
        // 1. Remove <li> orphans (state nao contem mais)
        Array.from(ul.children).forEach((li) => {
          const id = li.dataset.variantId;
          if (id && !stateIds.has(id)) li.remove();
        });

        // 2. Atualiza ou cria <li> para cada entry no state
        [...this.state.entries()].forEach(([id, v]) => {
          let li = ul.querySelector(`[data-variant-id="${CSS.escape(id)}"]`);
          if (!li) {
            // Item novo — cria <li> (animation kit-item-enter dispara so aqui)
            li = document.createElement('li');
            li.className = 'kit-cesta__item';
            li.dataset.variantId = id;
            li.innerHTML = `
              <img class="kit-cesta__item-image" src="${v.image}" alt="" loading="lazy">
              <div class="kit-cesta__item-info">
                <p class="kit-cesta__item-title">${this._escape(v.title)}</p>
                <div class="kit-cesta__item-row">
                  <span class="kit-cesta__item-price" data-kit-item-price>${v.qty}× ${money(v.kitPrice)}</span>
                  <button type="button" class="link text-xs" data-kit-remove>Remover</button>
                </div>
              </div>
            `;
            li.querySelector('[data-kit-remove]').addEventListener('click', () => {
              const card = this.cards.find((c) => c.dataset.variantId === id);
              if (card) this.setQty(card, 0);
            });
            ul.appendChild(li);
          } else {
            // Item ja existe — apenas atualiza texto-qty (sem re-criar DOM, sem flicker)
            const priceEl = li.querySelector('[data-kit-item-price]');
            if (priceEl) priceEl.textContent = `${v.qty}× ${money(v.kitPrice)}`;
          }
        });
      });
    }

    _escape(s) {
      return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    openDrawer() {
      if (this.drawer && typeof this.drawer.show === 'function') this.drawer.show();
    }

    async submit() {
      if (this.submitting) return;
      if (this.totalQty() !== this.kitSize) return;

      this.submitting = true;
      this.render();

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const kitId = uuid();
      const items = [...this.state.entries()].map(([id, v]) => ({
        id: parseInt(id, 10),
        quantity: v.qty,
        properties: {
          _kit_id: kitId,
          _kit_size: String(this.kitSize),
          _kit_name: this.kitLabel,
          _kit_unit_label: this.kitUnit,
        },
      }));

      try {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

        // 1. Coleta sections que precisam re-render (cart-drawer, cart-icon-bubble, etc).
        //    Mesmo protocolo do <product-form> nativo do tema (theme.js L2772-2776).
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        // 2. POST /cart/add.js. Shopify aceita 2 formatos:
        //    - JSON body { items: [...], sections: "csv", sections_url: "..." }
        //    - multipart com items[i][id], items[i][quantity], items[i][properties][k]
        //    Usamos JSON body — mais simples para multi-item com properties aninhadas.
        //    Param `sections` faz o Shopify retornar HTML renderizado dessas sections.
        const r = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(','),
            sections_url: `${root}cart`,
          }),
        });
        const responseJson = await r.json().catch(() => ({}));
        if (!r.ok) {
          throw new Error(responseJson.description || responseJson.message || 'Não foi possível adicionar o kit.');
        }

        // 3. Pega cart.js completo + injeta sections renderizadas.
        //    Replica theme.js L2795-2796: cartContent.sections = responseJson.sections.
        const cartResp = await fetch(`${root}cart.js`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        const cart = cartResp.ok ? await cartResp.json() : {};
        cart.sections = responseJson.sections || {};

        // 4. Limpa state local + fecha drawer mobile ANTES do dispatch (cart-drawer abre limpo).
        this.clearAll();
        if (this.drawer && typeof this.drawer.hide === 'function') this.drawer.hide();

        // 5. Dispatch cart:change com cart.sections preenchido.
        //    <cart-drawer>.onCartChange (theme.js L4711) chama _replaceContent(cart.sections[sectionId])
        //    e em seguida this.show() porque baseEvent='variant:add' + onSuccessDo='force_open_drawer'.
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart },
          })
        );
      } catch (err) {
        console.error('[monte-seu-kit] submit error:', err);
        this.queryAllInRoots('[data-kit-feedback]').forEach((el) => (el.textContent = err.message || 'Erro ao adicionar.'));
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.submitting = false;
        this.render();
      }
    }

    persist() {
      try {
        if (this.state.size === 0) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        const data = [...this.state.entries()];
        localStorage.setItem(this.storageKey, JSON.stringify({ exp: Date.now() + TTL_MS, data }));
      } catch (_) {}
    }

    restore() {
      try {
        const raw = localStorage.getItem(this.storageKey);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (!parsed?.exp || parsed.exp < Date.now()) {
          localStorage.removeItem(this.storageKey);
          return;
        }
        for (const [id, v] of parsed.data) {
          const card = this.cards.find((c) => c.dataset.variantId === id);
          if (!card || card.dataset.outOfStock === 'true') continue;
          if (this.totalQty() + v.qty > this.kitSize) continue;
          this.state.set(id, v);
          this._setCardUI(card, v.qty);
        }
      } catch (_) {}
    }
  }

  window.customElements.define('monte-seu-kit', MonteSeuKit);
})();
